import { Link } from '@tanstack/react-router';
import { CheckCheck, FileText, ListChecks } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import {
  CRITERION_MARK_SYMBOLS,
  MANAGER_RESPONSE_LABELS,
  shortlistCounts,
  type ShortlistCandidate,
  type ShortlistView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Popover, usePopover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { Stars } from '../../../components/Stars';
import { ToneIcon, toneCardClass } from '../../../components/Tone';
import { MANAGER_RESPONSE_TONES } from '../../../components/status-tones';
import { EmptyState, ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatDate } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { shortlistCvUrl, useAnswerShortlist, useFinishShortlist, useShortlist, type AnswerChoice } from '../api/shortlist-api';
import { AnswerControls } from '../components/AnswerControls';
import { RemarkThread } from '../../remarks/components/RemarkThread';
import styles from '../components/shortlist.module.css';

/** The mark for a salary line the viewer may not see: not a tick, a half or a cross, only that it is there. */
const HIDDEN_MARK_SYMBOL = '—';

export interface ManagerShortlistSearch {
  /** N-89: only the names added by this send. */
  readonly send?: string;
}

/**
 * SHL-01 — the manager's yes or no on each shortlisted person (§L31, F24.02, F24.03), built for a
 * phone first (375px). N-88, N-89 and N-90 open here. Reached by being the job's Requested by or
 * Line manager, whatever the person's role (§L23, DECISIONS-LOG "Start R6" Q1).
 */
export function ManagerShortlistPage({ jobId, search }: { readonly jobId: string; readonly search: ManagerShortlistSearch }): ReactNode {
  const shortlist = useShortlist(jobId, 'manager');
  const isManagerStepOn = useModule('manager_shortlist');
  // ADR 0082: an old email's link, with "Shortlist to the line manager" switched off since.
  if (!isManagerStepOn) {
    return (
      <EmptyState
        title="Nothing to answer here"
        body="HR no longer sends shortlists to managers, so there is nothing for you to say yes or no to. They move people on themselves."
        action={
          <Link to="/my/actions" className={styles.action}>
            Go to My actions
          </Link>
        }
      />
    );
  }
  if (shortlist.isPending) return <ShortlistSkeleton />;
  if (shortlist.error) {
    if (shortlist.error.status === 403) return <ForbiddenState resource="this shortlist" contact="The job's recruiter" />;
    if (shortlist.error.status === 404) return <NotFoundState />;
    return <ServerErrorState onRetry={() => void shortlist.refetch()} />;
  }
  return <Shortlist view={shortlist.data} jobId={jobId} sendId={search.send ?? null} />;
}

function Shortlist({ view, jobId, sendId }: { readonly view: ShortlistView; readonly jobId: string; readonly sendId: string | null }): ReactNode {
  const { answer, pending } = useAnswerShortlist(jobId);
  const { finish, isFinishing } = useFinishShortlist(jobId);
  const [drafts, setDrafts] = useState<ReadonlyMap<string, AnswerChoice>>(new Map());
  const setDraft = (applicationId: string, draft: AnswerChoice | null): void =>
    setDrafts((current) => {
      if (!draft && !current.has(applicationId)) return current;
      const next = new Map(current);
      if (draft) next.set(applicationId, draft);
      else next.delete(applicationId);
      return next;
    });
  const saveDraft = async (candidate: ShortlistCandidate, choice: AnswerChoice): Promise<boolean> => {
    const isSaved = await answer(candidate, choice);
    if (isSaved) setDraft(candidate.applicationId, null);
    return isSaved;
  };
  const finishAndForget = async (): Promise<boolean> => {
    const isDone = await finish();
    if (isDone) setDrafts(new Map());
    return isDone;
  };
  const send =view.sends.find((candidate) => candidate.id === sendId) ?? null;
  const shown = useMemo(() => (send ? view.candidates.filter((candidate) => candidate.shortlistId === send.id) : view.candidates), [send, view.candidates]);
  const counts = shortlistCounts(view.candidates);
  // F24.03 "at least one answer": among the people whose send is still open (Q3).
  const open = shortlistCounts(view.candidates.filter((candidate) => !candidate.isLocked));
  const canFinish = open.yes + open.no > 0;
  const lastSend = view.sends.at(-1) ?? null;
  const { viewer } = view;

  return (
    <div className={styles.manager}>
      <header className={styles.managerHead}>
        <p className={styles.eyebrow}>Shortlist</p>
        <h1 className={styles.title}>{view.job.title}</h1>
        <p className={styles.meta}>
          {[view.job.reference, view.job.recruiterName ? `From ${view.job.recruiterName}` : null, lastSend ? `sent ${formatDate(lastSend.sentAt)}` : null].filter(Boolean).join(' · ')}
        </p>
        {send && (
          <p className={styles.notice}>
            Showing the {shown.length} {shown.length === 1 ? 'name' : 'names'} added on {formatDate(send.sentAt)}.{' '}
            <Link to="/my/shortlists/$jobId" params={{ jobId }} search={{}} className={styles.inlineLink}>
              Show everyone
            </Link>
          </p>
        )}
        {viewer.actor === 'hr_lead' && <p className={styles.notice}>You are answering on behalf of {view.recipients.map((recipient) => recipient.fullName).join(' and ') || 'the manager'}. It is recorded with your name.</p>}
        {viewer.actor === 'viewer' && <p className={styles.notice}>You can read this shortlist. {view.recipients.map((recipient) => recipient.fullName).join(' and ')} answer it.</p>}
        {view.isDone && viewer.actor === 'owner' && <p className={styles.notice}>You pressed Done. To change an answer now, ask {view.job.recruiterName ?? 'HR'}.</p>}
      </header>

      {/* R8 (DECISIONS-LOG "Start R8" Q3): where N-92 brings the requester and line manager. */}
      {view.selected.length > 0 && (
        <section className={styles.selected} aria-labelledby="shortlist-selected">
          <h2 id="shortlist-selected" className={styles.sectionTitle}>
            Selected
          </h2>
          <ul className={styles.selectedList}>
            {view.selected.map((person) => (
              <li key={person.applicationId} className={`${styles.selectedItem} ${toneCardClass()}`} data-tone="done">
                <span className={styles.selectedName}>{person.candidate.fullName}</span>
                <span className={styles.meta}>Selected {formatDate(person.selectedAt)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {shown.length === 0 && view.selected.length === 0 ? (
        <EmptyState
          title="Nobody on this shortlist yet"
          body="HR hasn't sent anyone. You'll get an email when they do."
          action={
            <Link to="/" className={styles.action}>
              Back to my home
            </Link>
          }
          isInset
        />
      ) : (
        <ol className={styles.people}>
          {shown.map((candidate) => (
            <PersonCard
              key={candidate.applicationId}
              candidate={candidate}
              jobId={jobId}
              view={view}
              isPending={pending.has(candidate.applicationId)}
              onAnswer={(choice) => answer(candidate, choice)}
              onDraft={(draft) => setDraft(candidate.applicationId, draft)}
            />
          ))}
        </ol>
      )}

      <div className={styles.doneBar} role="region" aria-label="Your answers">
        <span className={styles.doneCounts}>
          {counts.yes} yes · {counts.no} no · {counts.awaiting} waiting
        </span>
        {view.isDone ? (
          <span className={styles.doneMark}>
            <CheckCheck size={16} aria-hidden /> Done
          </span>
        ) : (
          viewer.mayFinish && <DoneButton candidates={view.candidates} drafts={drafts} canFinish={canFinish} isFinishing={isFinishing} onAnswer={saveDraft} onFinish={finishAndForget} />
        )}
      </div>
    </div>
  );
}

interface DoneButtonProps {
  readonly candidates: readonly ShortlistCandidate[];
  readonly drafts: ReadonlyMap<string, AnswerChoice>;
  readonly canFinish: boolean;
  readonly isFinishing: boolean;
  readonly onAnswer: (candidate: ShortlistCandidate, choice: AnswerChoice) => Promise<boolean>;
  readonly onFinish: () => Promise<boolean>;
}

/**
 * F24.03's Done. Done with someone unanswered is allowed (§L31), but a No typed and not saved is
 * never lost silently (D5): Done asks, beside itself, whether to save it or let it go — one more
 * press, and only when there is such a No. It is not saved by itself: a No starts HR's rejection,
 * so only the manager's own press may send it.
 */
function DoneButton({ candidates, drafts, canFinish, isFinishing, onAnswer, onFinish }: DoneButtonProps): ReactNode {
  const { anchor, triggerRef, open, close } = usePopover();
  const [isSaving, setIsSaving] = useState(false);
  const unsaved = candidates.flatMap((candidate) => {
    const choice = drafts.get(candidate.applicationId);
    return choice && !candidate.isLocked ? [{ candidate, choice }] : [];
  });
  const names = unsaved.map((entry) => entry.candidate.candidate.fullName).join(', ');

  async function saveAndFinish(): Promise<void> {
    setIsSaving(true);
    let isEverySaved = true;
    for (const entry of unsaved) {
      if (!(await onAnswer(entry.candidate, entry.choice))) isEverySaved = false;
    }
    setIsSaving(false);
    close();
    // A No the server refused stays on its card with its toast; Done waits for it.
    if (isEverySaved) await onFinish();
  }

  return (
    <>
      <Button
        ref={triggerRef}
        variant="solid"
        icon={ListChecks}
        disabled={!canFinish || isFinishing || isSaving}
        onClick={() => (unsaved.length > 0 ? open() : void onFinish())}
        title={canFinish ? undefined : 'Answer at least one new person first'}
      >
        Done
      </Button>
      <Popover anchor={anchor} onClose={close} label="Unsaved No" align="right">
        <div className={styles.confirm}>
          <p className={styles.confirmText}>
            You typed a No for {names} but didn’t save it.
          </p>
          <div className={styles.noActions}>
            <Button
              variant="ghost"
              disabled={isSaving}
              onClick={() => {
                close();
                void onFinish();
              }}
            >
              Discard and finish
            </Button>
            <Button variant="dangerSolid" disabled={isSaving} onClick={() => void saveAndFinish()}>
              Save No and finish
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}

function PersonCard({
  candidate,
  jobId,
  view,
  isPending,
  onAnswer,
  onDraft,
}: {
  readonly candidate: ShortlistCandidate;
  readonly jobId: string;
  readonly view: ShortlistView;
  readonly isPending: boolean;
  readonly onAnswer: Parameters<typeof AnswerControls>[0]['onAnswer'];
  readonly onDraft: (draft: AnswerChoice | null) => void;
}): ReactNode {
  const { viewer } = view;
  const mayAnswer = viewer.mayAnswer && (viewer.actor !== 'owner' || !candidate.isLocked) && candidate.answer !== 'hr_proceeded';
  const isAnswered = candidate.answer === 'yes' || candidate.answer === 'no';
  return (
    <li className={`${styles.person} ${toneCardClass()}`} data-tone={MANAGER_RESPONSE_TONES[candidate.answer]} data-pending={isPending || undefined}>
      <ToneIcon tone={MANAGER_RESPONSE_TONES[candidate.answer]} />
      <div className={styles.personHead}>
        <h2 className={styles.personName}>
          {/* Owner, 2026-10-18 (Group 1 Q1): a hiring manager opens a candidate on their own jobs, read only. */}
          <Link to="/candidates/$candidateId" params={{ candidateId: candidate.candidate.id }}>
            {candidate.candidate.fullName}
          </Link>
        </h2>
        <Chip tone={MANAGER_RESPONSE_TONES[candidate.answer]}>{MANAGER_RESPONSE_LABELS[candidate.answer]}</Chip>
      </div>
      <div className={styles.scores}>
        <Stars value={candidate.stars} />
        <span className={styles.score} title="Match with the ideal candidate">
          {candidate.score ?? '—'}
        </span>
        <span className={styles.hrStars}>HR ★ {candidate.hrStars === null ? '—' : candidate.hrStars.toFixed(1)}</span>
      </div>
      {/* ADR 0049: HR's Team remarks travel with the person (they replace the single HR note). */}
      {candidate.remarks.length > 0 && <RemarkThread remarks={candidate.remarks} />}
      {candidate.topMatches.length > 0 && (
        <ul className={styles.marks} aria-label="Top 3 lines for this job">
          {candidate.topMatches.map((match) => (
            <li key={match.label} className={styles.mark} data-mark={match.mark}>
              <span aria-hidden>{match.mark === 'hidden' ? HIDDEN_MARK_SYMBOL : CRITERION_MARK_SYMBOLS[match.mark]}</span> {match.label}
              {match.mark === 'hidden' && <span className={styles.markNote}> hidden from you</span>}
            </li>
          ))}
        </ul>
      )}
      {candidate.cvDocumentId && (
        <a className={styles.cvLink} href={shortlistCvUrl(jobId, candidate.applicationId)} target="_blank" rel="noreferrer">
          <FileText size={16} aria-hidden /> Open CV
        </a>
      )}
      {isAnswered && candidate.answeredByName && (
        <p className={styles.by}>
          {candidate.answer === 'no' && candidate.reason ? `No — “${candidate.reason}”${candidate.reasonLabel ? ` (${candidate.reasonLabel})` : ''}. ` : ''}
          Answered by {candidate.answeredByName}
          {candidate.isOnBehalf ? ' for the manager' : ''}
          {candidate.answeredAt ? `, ${formatDate(candidate.answeredAt)}` : ''}.
        </p>
      )}
      {mayAnswer && <AnswerControls key={`${candidate.applicationId}-${candidate.version}`} candidate={candidate} reasons={view.reasons} isPending={isPending} onAnswer={onAnswer} onDraft={onDraft} />}
    </li>
  );
}

function ShortlistSkeleton(): ReactNode {
  return (
    <div className={styles.manager} aria-busy="true">
      <SkeletonLines count={3} />
      <SkeletonLines count={6} />
    </div>
  );
}
